import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-blush px-4" data-testid="reset-password-page">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-25 mix-blend-multiply bg-cover bg-top"
        style={{ backgroundImage: "url(/brand/watercolour.jpg)" }}
      />
      <img src="/brand/floral-tl.png" alt="" aria-hidden className="pointer-events-none absolute left-0 top-0 w-48 opacity-80" />
      <img src="/brand/floral-br.png" alt="" aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-56 opacity-80" />

      <div className="relative z-10 w-full max-w-sm rounded-3xl border border-gold/40 bg-white/85 p-8 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.4)] backdrop-blur">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/brand/logo.png" alt="Miss Cupcake Creations logo" className="h-20 w-20 rounded-full ring-1 ring-gold/50" />
          <h1 className="mt-4 font-serif text-2xl font-semibold text-burgundy">Choose a New Password</h1>
        </div>

        {!token ? (
          <p className="text-center text-sm text-red-700" data-testid="reset-no-token">
            This reset link is missing its token — please use the full link from your email.
          </p>
        ) : done ? (
          <div className="text-center" data-testid="reset-success-message">
            <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-gold" />
            <p className="text-sm text-plum/80">Your password has been updated.</p>
            <a
              href="/admin/login"
              data-testid="reset-go-login"
              className="mt-5 inline-block rounded-full border border-gold bg-gold px-8 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-all hover:-translate-y-0.5 hover:bg-golddeep"
            >
              Sign in
            </a>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/70" htmlFor="reset-password">
              New password
            </label>
            <input
              id="reset-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              className="mb-4 w-full rounded-xl border border-gold/40 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
              placeholder="At least 8 characters"
            />
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/70" htmlFor="reset-confirm">
              Confirm new password
            </label>
            <input
              id="reset-confirm"
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-input"
              className="mb-5 w-full rounded-xl border border-gold/40 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
              placeholder="Same password again"
            />
            {error && (
              <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="reset-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full border border-gold bg-gold py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-all hover:-translate-y-0.5 hover:bg-golddeep disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Update password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
